import {
  ConditionOperator,
  FieldCondition,
  FieldKind,
  OfferLetterTemplateField,
} from './types';

// Both formats, and they go to different backends (AXHE-4685). A .docx is parsed
// for variables and conditional blocks and becomes an OfferLetterTemplate. A PDF
// has neither, so it becomes an EmbeddedSignatureTemplate instead — the signature
// provider holds the file and the field placements. `isPdfUpload` is the fork.
export const ACCEPTED_UPLOAD_TYPES = '.docx,.pdf';

// Which of the two flows a chosen file takes. Extension-only on purpose: the
// authoritative check is the file's leading bytes and only the server can make
// it (EmbeddedSignatureTemplates::CreateDraftFromUpload). This decides which
// screen to open, not whether the document is valid.
export const isPdfUpload = (file: File): boolean =>
  file.name.toLowerCase().endsWith('.pdf');

// The roster a PDF template starts with. A signature template needs at least one
// signer, and on every document Fountain sends that signer is the applicant.
export const DEFAULT_PDF_SIGNER_ROLES = ['Applicant'];

// Mirrors OfferLetterTemplates::FieldCategory (AXHE-4180). The catalog's groups
// are now a FIXED set describing what a value is about, so — unlike the old
// "Applicant data fields" / "General merge keys" / one-group-per-attribute-entity
// shape — every category the backend can emit is enumerable here. Customer
// attributes fold into Opening (they all resolve from the opening) rather than
// minting a group named from account data.
export const APPLICANT_CATEGORY = 'Applicant';
export const OPENING_CATEGORY = 'Opening';
export const OFFER_CATEGORY = 'Offer';
export const COMPANY_CATEGORY = 'Company';
export const HIRING_PROCESS_CATEGORY = 'Hiring process';

export const CONDITIONAL_KIND: FieldKind = 'conditional_toggle';

// A merge key as a human-readable row name: `BASE_SALARY` -> "Base salary".
// Callers prefer the field's own `label` and fall back to this.
//
// One copy, because there were two identical ones (both rails) and the save
// guard now needs a third to name the row it is refusing (AXHE-4366 §3).
export const humanizeKey = (key: string): string =>
  key
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());

// Signature placeholders auto-resolve to a Dropbox Sign text tag at render time
// (no admin mapping, no value), so they don't belong in the Variables rail's
// "map a value" list. Authoring/placement lives in the Signatures rail (and
// AXHE-3751). AXHE-3852 review.
export const SIGNATURE_KIND: FieldKind = 'signature';

// The roster role key a `[SIGNATURE_<ROLE>]` placeholder names, or null for a
// bare `[SIGNATURE]` — which is always the candidate and needs no suffix.
// Downcased, because roster keys are `slug()`ed lower-case. Mirrors
// OfferLetterTemplateField#signature_role_suffix.
//
// One copy, for the three questions asked of a placeholder — which are one
// parse, exactly as they are on the backend, where `#signature_role` and
// `#signature_role_resolvable?` share a private suffix method rather than
// spelling the pattern twice:
//
//   * which signer does it place? (`signatureFieldRole`, AXHE-4391)
//   * does that signer exist on the roster? (`unresolvableSignatureRoles`,
//     AXHE-4376 — the editor warning)
//   * what do we CALL the role that doesn't? (`SendOffer/readiness.ts`,
//     AXHE-4441 — the blocked send)
//
// It lives here rather than in the rail because the third caller is a different
// container, and two containers sharing a rule is what this module is for.
//
// Callers must establish that the field is a signature field before asking —
// `kind`, or a readiness reason only signature fields can produce. The key's
// shape does not settle it: FieldDetector gives a key in the account's own
// merge-key catalog precedence over the SIGNATURE prefix, so an account already
// using `SIGNATURE_DATE` as a data key keeps it as data (AXHE-3852 review).
export const signatureRoleSuffix = (key: string): string | null =>
  /^SIGNATURE_(.+)$/.exec(key)?.[1].toLowerCase() ?? null;

// A MAPPABLE VARIABLE — what the Variables rail lists, and therefore what the
// Variables badge above it must count. Conditional blocks live in the
// Conditions rail; signature placeholders auto-resolve at render time
// (Signatures rail) — neither is a variable an admin can map to a Fountain
// field.
//
// One definition, because the rule had already drifted (AXHE-4374): the rail
// excluded both kinds, the badge labelling it excluded only conditionals, so
// the badge read 10 over a list of 9 — overstating by the number of signature
// placeholders, which is at least one on every real template. Mirrors
// OfferLetterTemplateField#variable?.
export const isMappableVariable = (field: OfferLetterTemplateField): boolean =>
  field.kind !== CONDITIONAL_KIND && field.kind !== SIGNATURE_KIND;

// Kinds an admin can assign to a non-conditional variable.
export const EDITABLE_FIELD_KINDS: Exclude<FieldKind, 'conditional_toggle'>[] = [
  'auto_readonly',
  'manual_entry',
  'free_text',
];

// Mirrors OfferLetterTemplateFieldCondition::OPERATORS.
export const CONDITION_OPERATORS: ConditionOperator[] = [
  'equals',
  'not_equals',
  'present',
  'blank',
  'greater_than',
  'less_than',
  'contains',
  'in',
  'not_in',
];

// Presence-only operators — value input hidden. Mirrors VALUELESS_OPERATORS.
export const VALUELESS_OPERATORS: ConditionOperator[] = ['present', 'blank'];

// Operators whose value is a LIST of entries (AXHE-4066).
// Mirrors OfferLetterTemplateFieldCondition::LIST_OPERATORS.
export const LIST_OPERATORS: ConditionOperator[] = ['in', 'not_in'];

// Rails' `blank?` for a string value, which is what every server-side presence
// check on a condition actually applies — `validates :value, presence: true`
// rejects "   " exactly as it rejects "".
//
// The one definition of "counts as answered" on the client (Reef, #3353).
// Whitespace-only is the case both surfaces got wrong independently and in
// opposite directions: the editor accepted a row Rails then 422s, and Send
// Offer's overlay kept a value the server had already treated as unanswered, so
// the browser revealed a field the server considered hidden. Same missing
// `.trim()`, two symptoms — which is the argument for it living in one place
// rather than being fixed twice.
//
// Takes a bare string so `SendOffer/visibility.ts` can share it without also
// importing this module's operator table, which it deliberately mirrors rather
// than imports (see that file's docstring — the mirror is what its
// exhaustiveness test pins).
export const isBlankConditionValue = (value: string | null | undefined): boolean =>
  (value ?? '').trim() === '';

// A stored JSON array of strings, the form the server writes when an entry
// holds a comma (`["Dallas, TX"]`), or null for anything else. Mirrors
// OfferLetterTemplateFieldCondition.decode_list_value: an array with no comma
// in any entry is not that form, so a hand-typed `["Remote"]` stays one entry.
const decodeListValue = (text: string): string[] | null => {
  if (!text.startsWith('[')) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  if (!parsed.every((entry): entry is string => typeof entry === 'string')) return null;
  if (!parsed.some(entry => entry.includes(','))) return null;

  return parsed.map(entry => entry.trim()).filter(entry => entry !== '');
};

// "CA, ND, OK" -> ["CA", "ND", "OK"]. Whitespace around entries is an authoring
// artifact; empty entries (a trailing comma) are dropped rather than becoming a
// "" nobody can match. Mirrors
// OfferLetterTemplateFieldCondition.split_list_value, JSON form included.
export const splitListValue = (value: string | null | undefined): string[] => {
  const text = (value ?? '').trim();

  return (
    decodeListValue(text) ??
    text
      .split(',')
      .map(entry => entry.trim())
      .filter(entry => entry !== '')
  );
};

// A list operator's entries. `values` arrives already split from the server
// and is what the builder writes, because only the array can hold an entry
// that contains a comma (`Dallas, TX`). Splitting `value` is the fallback for a
// row that carries no `values`.
export const listEntries = (condition: FieldCondition): string[] =>
  condition.values ?? splitListValue(condition.value);

// AXHE-4543. A formula's typographic operator substitutes — what a paste out
// of Word carries instead of what a keyboard types. Shared by the editor's
// authoring control (Editor/formulaAuthoring.ts, normalizes on input and on
// save) and the Send Offer evaluator (formula.ts, normalizes at evaluation
// time): `formulas` had no server validation before this ticket, so a row
// already stored with one of these — the free-text hint used to tell admins
// to type exactly this — must still parse, not just a freshly authored one.
const FORMULA_OPERATOR_SUBSTITUTIONS: [string, string][] = [
  ['−', '-'], // U+2212 minus sign
  ['×', '*'], // U+00D7 multiplication sign
  ['÷', '/'], // U+00F7 division sign
];

export const normalizeFormulaOperators = (value: string): string =>
  FORMULA_OPERATOR_SUBSTITUTIONS.reduce((str, [from, to]) => str.split(from).join(to), value);

// A condition row the server will actually accept: a data key, and — unless the
// operator is presence-only — a value. Mirrors
// OfferLetterTemplateFieldCondition's `validates :data_key, presence: true`,
// `validates :value, presence: true, unless: :valueless_operator?`, and
// `list_value_has_entries`.
//
// One definition, because two callers need exactly this predicate and they need
// the SAME one (jdmurphy, #3353): the canvas's "unconfigured" highlight, and
// the Save guard. A copy per caller is a rule that can drift into the editor
// warning about one set of rows and refusing to post a different set.
//
// The QUANTIFIER is the callers' business and differs between them, so it is
// deliberately not baked in here: a block is configured once ANY row is
// complete, but a save must post rows that are ALL complete, because
// SaveFieldConditions raises RecordInvalid on one bad row and rolls the whole
// set back.
//
// A LIST operator needs a separate test, not just a non-blank value: `","` and
// `" , "` are non-blank but split to nothing, and the server rejects them for a
// reason worth repeating — a list of nothing but separators can never match, so
// it would pin the rule off (`in`) or on (`not_in`) forever.
export const isConditionComplete = (condition: FieldCondition): boolean => {
  if (!condition.data_key) return false;
  if (condition.operator != null && VALUELESS_OPERATORS.includes(condition.operator)) {
    return true;
  }
  if (condition.operator != null && LIST_OPERATORS.includes(condition.operator)) {
    return listEntries(condition).some(entry => entry.trim() !== '');
  }
  return !isBlankConditionValue(condition.value);
};

// CONFIGURED — a rule set that will actually decide something, which is the
// `some` half of the quantifier `isConditionComplete` deliberately leaves out
// above. Conditions on a field are OR-combined, so one usable row is a working
// rule no matter how many half-typed ones sit beside it. The `every` half stays
// at the Save guard, where the question is "will the server take all of this".
//
// Takes the ROWS rather than the field, because the four callers read them from
// three different places and all four have to agree (AXHE-4389):
//
//   - the block accordion's ⚠, from its live edit buffer
//   - the conditional blocks rail's unconfigured-first sort, from the persisted set
//   - the canvas's "unconfigured block" highlight, from the persisted set
//   - the Blocks tab badge's numerator, from the DRAFT where this session has
//     one and the persisted set otherwise
//
// The badge is why this stopped being private to ConditionalBlocksRail. It sits
// directly above those ⚠ marks, so a badge deriving "configured" its own way is
// AXHE-4374 again one rail over: a number disagreeing with the list it labels.
//
// `VariableRow`'s "shown when" summary spells the same `some` inline and is
// deliberately NOT converted: AXHE-4474 (#4132) deletes that rule builder, so
// folding it in here would only conflict with the change removing it.
export const hasCompleteCondition = (conditions: FieldCondition[] | undefined): boolean =>
  (conditions ?? []).some(isConditionComplete);

/** The shape `<input type="date">` accepts. Necessary but not sufficient. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// Whether a date input can display this value. A browser clears a date input
// holding a day that does not exist, which renders a blank control over a
// condition that still holds its value.
//
// Shape alone is not enough: `Date` rolls an impossible day forward
// (`2026-02-30` parses to `2026-03-02`), so a value passes only when it comes
// back unchanged. The NaN guard covers an unparseable month or day, where
// `toISOString` would throw. HTML's date grammar requires a year above zero,
// which the round trip cannot catch, hence the `0000` check.
//
// SendOfferForm carries its own copy of this check.
export const isDisplayableDate = (value: string): boolean => {
  if (!ISO_DATE.test(value) || value.startsWith('0000')) return false;
  // Pinned to UTC so the round trip can't be shifted a day by the local zone.
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
};
